'use client';

import { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Value {
  id: string;
  icon: string;
  title: string;
  description: string;
  color: string;
}

export default function ValuesSection() {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -5% 0px' }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const values: Value[] = [
    {
      id: 'value_excellence',
      icon: 'StarIcon',
      title: 'Excellence',
      description:
        'We strive for excellence in every project, ensuring the highest quality standards and attention to detail in all our deliverables.',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      id: 'value_innovation',
      icon: 'LightBulbIcon',
      title: 'Innovation',
      description:
        'Staying ahead of technology trends, we continuously innovate to provide cutting-edge solutions that give our clients a competitive advantage.',
      color: 'from-purple-500 to-pink-500',
    },
    {
      id: 'value_integrity',
      icon: 'ShieldCheckIcon',
      title: 'Integrity',
      description:
        'Transparency and honesty guide our relationships with clients, partners, and team members, building trust that lasts.',
      color: 'from-green-500 to-emerald-500',
    },
    {
      id: 'value_collaboration',
      icon: 'UserGroupIcon',
      title: 'Collaboration',
      description:
        'We believe in working closely with our clients as partners to achieve shared success and create lasting value together.',
      color: 'from-orange-500 to-red-500',
    },
  ];

  return (
    <section className="py-20 lg:py-32 relative z-10 bg-gray-950">
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="text-center mb-16 animate-on-scroll animate-in">
          <span className="text-xs uppercase tracking-[0.4em] text-blue-400 mb-4 block">
            Our Values
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Principles That <span className="text-gradient-blue">Define Us</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Our core values shape everything we do and guide our commitment to excellence
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {values.map((value, index) => (
            <div
              key={value.id}
              className={`group relative bg-gray-900 border border-zinc-800 rounded-2xl p-8 hover:border-transparent transition-all duration-300 animate-on-scroll animate-in-delay-${
                index + 1
              }`}
            >
              {/* Gradient overlay on hover */}
              <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${value.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
              
              <div className="relative z-10">
                <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${value.color} flex items-center justify-center text-white mb-6 shadow-lg`}>
                  <Icon name={value.icon as any} size={32} />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">
                  {value.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {value.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}